// frontend/src/components/Sidebar.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Users,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Home,
  Search,
  CalendarDays,
  MessageCircle,
  LogOut,
  Sun,
  Moon,
  TrendingUp,
  Bot,
  Eye,
  EyeOff,
  Bell,
} from "lucide-react";
import Avatar from "./Avatar";
import { displayName } from "../utils/displayName";
import StatusPicker from "./StatusPicker";
import logo from "../assets/teakonn-logo.png";
import { socket } from "../socket";
import dayjs from "dayjs";

// Use normalized absolute API base
import { API_URL } from "../config/api";
const API = API_URL;

interface SidebarProps {
  token: string;
  user: any;
  theme: string;
  myStatus: any;
  isOnline?: boolean;
  onNavigate?: (view: string) => void;
  onThemeToggle: () => void;
  onLogout: () => void;
  onStatusUpdated: (status: any) => void;
  onShowProfile: (user: any) => void;
  onEditProfile?: (user: any) => void;
  onOpenConversation: (conversation: any) => void;
  conversations: any[];
  makeAvatarUrl: (url?: string) => string;
}

export default function Sidebar({
  token,
  user,
  theme,
  myStatus,
  isOnline = false,
  onNavigate,
  onThemeToggle,
  onLogout,
  onStatusUpdated,
  onShowProfile,
  onEditProfile,
  onOpenConversation,
  conversations,
  makeAvatarUrl
}: SidebarProps) {
  const [followers, setFollowers] = useState(0);
  const [following, setFollowing] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [assistantHidden, setAssistantHidden] = useState<boolean>(() => {
    try { return localStorage.getItem('teakonn.assistantHidden') === 'true'; } catch { return false; }
  });
  const [joinedActiveEvents, setJoinedActiveEvents] = useState<any[]>([]);
  const [groupUnread, setGroupUnread] = useState<Record<string, number>>({});
  const [notifications, setNotifications] = useState<Array<{ id: string; title: string; message: string; time: string; date: string; }>>([]);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState<boolean>(true);
  const [unreadNotifs, setUnreadNotifs] = useState<number>(() => {
    try { return parseInt(localStorage.getItem('auralink-unread-notifs') || '0', 10) || 0; } catch { return 0; }
  });

  useEffect(() => {
    loadUserStats();
    // Initial load of group chats and polling
    loadGroupChats();
    // Load notifications: prefer snapshot from Notifications page if available
    try {
      const uid = String(user?._id || user?.id || '');
      if (uid) {
        const snapRaw = localStorage.getItem(`auralink-notifs-visible-snapshot:${uid}`) || '';
        if (snapRaw) {
          const list = JSON.parse(snapRaw || '[]');
          const mapped = Array.isArray(list) ? list.map((n: any) => ({
            id: String(n.id),
            title: n.title || '',
            message: n.message || '',
            time: dayjs(n.date || new Date()).fromNow(),
            date: n.date || new Date().toISOString(),
            kind: n.kind,
            avatar: n.avatar,
          })) : [];
          setNotifications(mapped);
          try {
            const count = mapped.length;
            setUnreadNotifs(count);
            localStorage.setItem('auralink-unread-notifs', String(count));
            window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count } }));
          } catch {}
        } else {
          loadNotifications();
        }
      } else {
        loadNotifications();
      }
    } catch { loadNotifications(); }
    const interval = setInterval(() => {
      loadGroupChats();
      // If a snapshot exists, keep mirroring it; otherwise fallback to polling
      try {
        const uid = String(user?._id || user?.id || '');
        const snapRaw = uid ? localStorage.getItem(`auralink-notifs-visible-snapshot:${uid}`) || '' : '';
        if (snapRaw) {
          const list = JSON.parse(snapRaw || '[]');
          const mapped = Array.isArray(list) ? list.map((n: any) => ({
            id: String(n.id),
            title: n.title || '',
            message: n.message || '',
            time: dayjs(n.date || new Date()).fromNow(),
            date: n.date || new Date().toISOString(),
            kind: n.kind,
            avatar: n.avatar,
          })) : [];
          setNotifications(mapped);
          try {
            const count = mapped.length;
            setUnreadNotifs(count);
            localStorage.setItem('auralink-unread-notifs', String(count));
            window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count } }));
          } catch {}
        } else {
          loadNotifications();
        }
      } catch { loadNotifications(); }
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Listen for unread notifications updates
  useEffect(() => {
    const handler = (e: any) => {
      try {
        const next = e?.detail?.count;
        if (typeof next === 'number') setUnreadNotifs(next);
        else {
          const raw = localStorage.getItem('auralink-unread-notifs') || '0';
          setUnreadNotifs(parseInt(raw, 10) || 0);
        }
      } catch {}
    };
    window.addEventListener('auralink-unread-notifs.update', handler as any);
    return () => window.removeEventListener('auralink-unread-notifs.update', handler as any);
  }, []);

  // Mirror Notifications page snapshot live
  useEffect(() => {
    const handler = (e: any) => {
      try {
        const uid = String(user?._id || user?.id || '');
        const from = e?.detail?.uid;
        const list = e?.detail?.list || [];
        if (!uid || (from && uid !== String(from))) return;
        const mapped = Array.isArray(list) ? list.map((n: any) => ({
          id: String(n.id),
          title: n.title || '',
          message: n.message || '',
          time: dayjs(n.date || new Date()).fromNow(),
          date: n.date || new Date().toISOString(),
          kind: n.kind,
          avatar: n.avatar,
        })) : [];
        setNotifications(mapped);
        try {
          const count = typeof e?.detail?.count === 'number' ? e.detail.count : mapped.length;
          setUnreadNotifs(count);
          localStorage.setItem('auralink-unread-notifs', String(count));
          window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count } }));
        } catch {}
      } catch {}
    };
    window.addEventListener('auralink-notifs.visible.update', handler as any);
    return () => window.removeEventListener('auralink-notifs.visible.update', handler as any);
  }, [user]);

  // Socket-driven refresh for incoming events
  useEffect(() => {
    try {
      const handler = () => loadGroupChats();
      socket?.on?.('receive_message', handler);
      socket?.on?.('message_deleted', handler);
      socket?.on?.('messages_bulk_deleted', handler);
      socket?.on?.('message_status_update', handler); // read/delivered updates
      return () => {
        socket?.off?.('receive_message', handler);
        socket?.off?.('message_deleted', handler);
        socket?.off?.('messages_bulk_deleted', handler);
        socket?.off?.('message_status_update', handler);
      };
    } catch {}
  }, []);

  async function loadUserStats() {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(false);
      console.log("Loading user stats from:", `${API}/users/me`);
      
      const res = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: (status) => status < 500,
      });
      
      console.log("User stats response:", res.status, res.data);
      
      if (res.status === 404) {
        console.warn("User endpoint not found (404)");
        setFollowers(0);
        setFollowing(0);
        setError(true);
        return;
      }
      
      const userData = res.data;
      const followersCount = Array.isArray(userData.followers) ? userData.followers.length : 0;
      const followingCount = Array.isArray(userData.following) ? userData.following.length : 0;
      
      console.log("Setting followers:", followersCount, "following:", followingCount);
      setFollowers(followersCount);
      setFollowing(followingCount);
    } catch (err: any) {
      console.error("Sidebar stats error:", err);
      setError(true);
      setFollowers(0);
      setFollowing(0);
    } finally {
      setLoading(false);
    }
  }

  async function loadGroupChats() {
    if (!token) return;
    try {
      const joinedRes = await axios.get(`${API}/events/my/joined`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const joined = (joinedRes.data?.events || []).filter((ev: any) => !ev.archivedAt);
      setJoinedActiveEvents(joined);

      const ids = joined.map((e: any) => e._id);
      if (ids.length > 0) {
        try {
          const unreadRes = await axios.post(
            `${API}/messages/rooms/unread-counts`,
            { rooms: ids },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setGroupUnread(unreadRes.data || {});
        } catch (e) {
          console.warn('Sidebar: failed to load group unread counts');
          setGroupUnread({});
        }
      } else {
        setGroupUnread({});
      }
    } catch (e) {
      console.warn('Sidebar: failed to load joined events');
      setJoinedActiveEvents([]);
      setGroupUnread({});
    }
  }

  async function loadNotifications() {
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/events?limit=50`, { headers });
      // Also fetch bookings and tokens + current user for followers to align with Notifications page content
      const [recvRes, sentRes, tokensRes, meRes] = await Promise.all([
        axios.get(`${API}/booking-requests/my/received`, { headers }),
        axios.get(`${API}/booking-requests/my/sent`, { headers }),
        axios.get(`${API}/tokens/my/received`, { headers }),
        axios.get(`${API}/users/me`, { headers }),
      ]).catch(() => [null, null, null, null] as any);
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const events: any[] = (res.data?.events || res.data || [])
        .filter((event: any) => {
          const d = new Date(event.startDate);
          return d >= now && d <= thirtyDaysFromNow;
        });

      // Deterministic seeded weighted selection: pick ONE prioritized notification
      function xmur3(str: string) {
        let h = 1779033703 ^ str.length;
        for (let i = 0; i < str.length; i++) {
          h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
          h = (h << 13) | (h >>> 19);
        }
        return function () {
          h = Math.imul(h ^ (h >>> 16), 2246822507);
          h = Math.imul(h ^ (h >>> 13), 3266489909);
          h ^= h >>> 16;
          return h >>> 0;
        };
      }
      function mulberry32(a: number) {
        return function () {
          let t = (a += 0x6d2b79f5);
          t = Math.imul(t ^ (t >>> 15), t | 1);
          t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
          return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
        };
      }
      function seededRandom(seed: string) {
        const seedFn = xmur3(seed);
        const a = seedFn();
        const rand = mulberry32(a);
        return rand();
      }
      const todayKey = dayjs(now).format('YYYY-MM-DD');
      const uid = String(user?._id || user?.id || 'anon');
      const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
      function scoreEvent(ev: any): number {
        const d = new Date(ev.startDate).getTime();
        const diff = Math.max(0, d - now.getTime());
        const recency = 1 - Math.min(1, diff / WINDOW_MS);
        const participants = (ev.participants?.length || 0);
        const cap = (ev.capacity?.max || 0);
        const denom = cap > 0 ? cap : Math.max(1, participants);
        const popularity = Math.min(1, participants / denom);
        const noise = seededRandom(uid + ':' + todayKey + ':' + String(ev._id || ev.id || 'x'));
        return recency * 0.6 + popularity * 0.3 + noise * 0.1;
      }

      // Prefer latest follower (unseen) on top if present
      let selected: any | null = null;
      try {
        const rawLast = localStorage.getItem('auralink-last-follower') || '';
        const last = rawLast ? JSON.parse(rawLast) : null;
        const uidSeenKey = `auralink-notifs-seen:${String(user?._id || user?.id || 'anon')}`;
        const seenRaw = localStorage.getItem(uidSeenKey) || '[]';
        const seenArr: string[] = Array.isArray(JSON.parse(seenRaw)) ? JSON.parse(seenRaw) : [];
        const followKey = last?.id ? `follow:${last.id}` : '';
        if (last && followKey && !seenArr.includes(followKey)) {
          selected = {
            id: last.id,
            title: 'New follower',
            message: `${last.username} followed you`,
            time: dayjs(last.at).fromNow(),
            date: new Date(last.at).toISOString(),
            kind: 'follow',
            avatar: last.avatar,
          };
        }
      } catch {}

      // Prefer booking notifications next (received pending first, then sent pending)
      let unseenCount = 0;
      let received: any[] = [];
      let sent: any[] = [];
      try {
        const uidSeenKey = `auralink-notifs-seen:${String(user?._id || user?.id || 'anon')}`;
        const rawSeen = localStorage.getItem(uidSeenKey) || '[]';
        const seenArr: string[] = Array.isArray(JSON.parse(rawSeen)) ? JSON.parse(rawSeen) : [];
        const seenSet = new Set(seenArr);
        const receivedAll: any[] = Array.isArray(recvRes?.data?.requests) ? recvRes!.data.requests : [];
        const sentAll: any[] = Array.isArray(sentRes?.data?.requests) ? sentRes!.data.requests : [];
        const tokens: any[] = Array.isArray(tokensRes?.data?.tokens) ? tokensRes!.data.tokens : [];
        const me = meRes?.data || {};
        const uidStr = String(user?._id || user?.id || 'anon');
        const listKeys = new Set<string>();
        for (const r of receivedAll) listKeys.add(`booking_received:${String(r._id)}`);
        // Token notifications from both sources
        for (const t of tokens) {
          const id = (t.request?._id || t.requestId || t.bookingRequest?._id || t.bookingRequestId || t._id || '').toString() + '-token';
          if (id) listKeys.add(`booking_token:${id}`);
        }
        for (const r of sentAll) {
          if (r.status === 'token_generated') listKeys.add(`booking_token:${String(r._id)}-token`);
        }
        // Followers: use persisted per-follower event date for seen keys
        const followersList: any[] = Array.isArray(me?.followers) ? me.followers : [];
        for (const u of followersList) {
          const fid = String(u?._id || u);
          let d = '';
          try { d = localStorage.getItem(`auralink-follow-date:${uidStr}:${fid}`) || ''; } catch {}
          const key = d ? `follow:${fid}:${d}` : `follow:${fid}`;
          listKeys.add(key);
        }
        // Count unseen from the unified list
        for (const k of listKeys) if (!seenSet.has(k)) unseenCount++;
        // Derive pending lists for selection
        received = receivedAll.filter((r: any) => r.status === 'pending');
        sent = sentAll.filter((r: any) => r.status === 'pending');
      } catch {}
      if (received.length > 0) {
        const r = received[0];
        selected = {
          id: String(r._id),
          title: `Booking request: ${r.venue?.name || 'your venue'}`,
          message: `From ${r.requester?.username || 'someone'}`,
          time: dayjs(r.createdAt).fromNow(),
          date: r.createdAt,
          kind: 'booking_received',
        };
      } else if (sent.length > 0) {
        const r = sent[0];
        selected = {
          id: String(r._id),
          title: `Your booking request to ${r.venue?.name || 'venue'}`,
          message: `Status: ${r.status || 'pending'}`,
          time: dayjs(r.createdAt).fromNow(),
          date: r.createdAt,
          kind: 'booking_sent',
        };
      }
      if (!selected && events.length > 0) {
        if (events.length === 1) selected = events[0];
        else {
          const weights = events.map((ev) => Math.max(0.001, scoreEvent(ev)));
          const total = weights.reduce((a, b) => a + b, 0);
          let t = seededRandom(uid + ':' + todayKey) * total;
          selected = events[0];
          for (let i = 0; i < events.length; i++) {
            t -= weights[i];
            if (t <= 0) { selected = events[i]; break; }
          }
        }
      }

      // Build list and hide any that have been viewed (seen) using user-specific keys
      const listRaw = selected ? (
        selected.kind ? [{ id: selected.id, title: selected.title, message: selected.message, time: selected.time, date: selected.date, kind: selected.kind, avatar: selected.avatar }] : [{
          id: String(selected._id),
          title: `Upcoming: ${selected.title}`,
          message: `${dayjs(selected.startDate).format('MMM D')} • ${selected.location?.city || selected.location?.name || 'TBA'}`,
          time: dayjs(selected.startDate).fromNow(),
          date: selected.startDate,
          kind: 'event',
        }]
      ) : [];
      let list = listRaw;
      try {
        const uidSeenKey = `auralink-notifs-seen:${String(user?._id || user?.id || 'anon')}`;
        const rawSeen = localStorage.getItem(uidSeenKey) || '[]';
        const arrSeen: string[] = Array.isArray(JSON.parse(rawSeen)) ? JSON.parse(rawSeen) : [];
        list = listRaw.filter((n) => {
          const k = n.kind ? `${n.kind}:${n.id}` : `${n.id}`;
          return !arrSeen.includes(k);
        });
      } catch {}
      setNotifications(list);
      // Update unread badge with unseen count (aligned to Notifications page)
      try {
        const next = unseenCount;
        setUnreadNotifs(next);
        localStorage.setItem('auralink-unread-notifs', String(next));
        window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: next } }));
      } catch {}
    } catch (e) {
      console.warn('Sidebar: failed to load notifications');
      setNotifications([]);
    }
  }

  const stats = [
    {
      icon: Users,
      label: "Followers",
      value: followers,
      color: "from-emerald-500/12 to-green-500/10",
      onClick: () => onNavigate?.('followers')
    },
    {
      icon: UserPlus,
      label: "Following",
      value: following,
      color: "from-emerald-500/12 to-green-500/10",
      onClick: () => onNavigate?.('following')
    }
  ];

  // Calculate total unread messages from conversations (only if conversations array exists and has items)
  const totalUnreadMessages = Array.isArray(conversations) && conversations.length > 0
    ? conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0) 
    : 0;
  const totalGroupUnread = Object.values(groupUnread || {}).reduce((s: number, n: any) => s + (n || 0), 0);
  
  console.log('[Sidebar] Conversations:', conversations?.length, 'Total unread:', totalUnreadMessages);

  // Helper component for navigation buttons
  const NavButton = ({ icon: Icon, label, badge, isCollapsed, onClick }: any) => (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group themed-card hover:shadow-md relative"
      title={isCollapsed ? label : ''}
    >
      <span className="relative">
        <Icon className="w-5 h-5 flex-shrink-0 text-theme-secondary" />
        {isCollapsed && badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-cyan-600 text-white text-[10px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {!isCollapsed && (
        <span className="flex-1 text-left text-sm font-medium text-heading">
          {label}
        </span>
      )}
      {!isCollapsed && badge !== undefined && badge > 0 && (
        <span className="px-2 py-0.5 bg-cyan-600 text-white text-xs rounded-full font-semibold">
          {badge > 999 ? '999+' : badge}
        </span>
      )}
    </button>
  );

  // Mobile toggle button
  const MobileToggle = () => (
    <>
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl border shadow-lg transition-all"
        style={{
          backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
          borderColor: theme === 'dark' ? '#475569' : '#cbd5e1'
        }}
      >
        {isMobileOpen ? (
          <X className="w-5 h-5" style={{ color: theme === 'dark' ? '#ffffff' : '#1e293b' }} />
        ) : (
          <Menu className="w-5 h-5" style={{ color: theme === 'dark' ? '#ffffff' : '#1e293b' }} />
        )}
      </button>
    </>
  );

  // Desktop collapse toggle
  const CollapseToggle = () => (
    <button
      onClick={() => setIsCollapsed(!isCollapsed)}
      className="hidden lg:flex absolute -right-3 top-8 p-1.5 rounded-full border shadow-lg transition-all z-10 themed-card"
    >
      {isCollapsed ? (
        <ChevronRight className="w-4 h-4" />
      ) : (
        <ChevronLeft className="w-4 h-4" />
      )}
    </button>
  );

  const SidebarContent = () => (
    <div className="h-full flex flex-col overflow-y-auto">
      {/* Header with Logo and Theme Toggle */}
      <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
        {!isCollapsed ? (
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center justify-center flex-1">
              <img src={logo} alt="TeaKonn" className="w-16 h-16 object-contain" />
            </div>
            {/* Theme Toggle */}
            <button
              onClick={onThemeToggle}
              className="p-2 rounded-lg themed-card hover:opacity-90 transition-all duration-300 group"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300 transition-colors" />
              ) : (
                <Moon className="w-5 h-5 text-slate-700 group-hover:text-slate-600 transition-colors" />
              )}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <img src={logo} alt="TeaKonn" className="w-10 h-10 object-contain" />
          </div>
        )}
      </div>

      {/* User Profile Section */}
      {!isCollapsed && (
        <div className="p-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => onEditProfile ? onEditProfile(user) : onShowProfile(user)}
              className="rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              title="Edit Profile"
            >
              <Avatar
                src={makeAvatarUrl(user?.avatar)}
                className="w-12 h-12 rounded-lg object-cover hover:opacity-90 transition"
                alt={user?.username || "User"}
              />
            </button>
              <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-bold truncate" style={{ color: 'var(--text)' }}>{displayName(user)}</div>
                {isOnline && (
                  <div className="flex-shrink-0 w-2.5 h-2.5 bg-green-500 rounded-full" title="Online"></div>
                )}
              </div>
              <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>{user?.username ? `@${user.username}` : ''}</div>
            </div>
          </div>

          {myStatus && (
            <div className="text-xs flex gap-1 items-center" style={{ color: 'var(--text-secondary)' }}>
              <span>{myStatus.emoji}</span>
              <span className="opacity-80 truncate">{myStatus.mood}</span>
            </div>
          )}
          <StatusPicker
            token={token}
            currentStatus={myStatus}
            onUpdated={onStatusUpdated}
          />

          {user?.about && (
            <div className="mt-3">
              <h4 className="text-xs font-semibold text-slate-300 mb-1">About</h4>
              <div className="text-xs text-theme-secondary whitespace-pre-line line-clamp-2" title={user.about}>
                {user.about}
              </div>
            </div>
          )}

          {/* Profile picture upload removed; use Edit Profile modal */}
        </div>
      )}

      {/* Navigation Menu */}
      <div className="p-2 space-y-2">
        {/* Priority: Posts, Direct Messages, Group Chats */}
        <NavButton
          icon={TrendingUp}
          label="Posts & Events"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('posts');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={MessageCircle}
          label="Direct Messages"
          badge={totalUnreadMessages}
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('direct-messages');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={Users}
          label="Group Chats"
          badge={totalGroupUnread}
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('group-chats');
            setIsMobileOpen(false);
          }}
        />

        {/* Other sections */}
        <NavButton
          icon={Home}
          label="Dashboard"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('dashboard');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={Search}
          label="Discover"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('discover');
            setIsMobileOpen(false);
          }}
        />
            <NavButton
          icon={Bell}
          label="Notifications"
          badge={unreadNotifs}
          isCollapsed={isCollapsed}
          onClick={() => {
                // Clear unread badge only; do NOT mark items seen here
                try {
                  localStorage.setItem('auralink-unread-notifs', '0');
                  window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: 0 } }));
                  localStorage.setItem('auralink-notifs-last-viewed', String(Date.now()));
                  setUnreadNotifs(0);
                } catch {}
            onNavigate?.('notifications');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={Search}
          label="Venue Booking"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('discover-venues');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={CalendarDays}
          label="My Activities"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('my-activities');
            setIsMobileOpen(false);
          }}
        />
        <NavButton
          icon={Users}
          label="People"
          isCollapsed={isCollapsed}
          onClick={() => {
            onNavigate?.('all-users');
            setIsMobileOpen(false);
          }}
        />
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <>
            <NavButton
              icon={Eye}
              label="Admin Overview"
              isCollapsed={isCollapsed}
              onClick={() => {
                onNavigate?.('admin');
                setIsMobileOpen(false);
              }}
            />
            <NavButton
              icon={Eye}
              label="Admin Stats"
              isCollapsed={isCollapsed}
              onClick={() => {
                onNavigate?.('admin-stats');
                setIsMobileOpen(false);
              }}
            />
            <NavButton
              icon={Eye}
              label="Admin Users"
              isCollapsed={isCollapsed}
              onClick={() => {
                onNavigate?.('admin-users');
                setIsMobileOpen(false);
              }}
            />
            <NavButton
              icon={Eye}
              label="Admin Flags"
              isCollapsed={isCollapsed}
              onClick={() => {
                onNavigate?.('admin-flags');
                setIsMobileOpen(false);
              }}
            />
          </>
        )}
      </div>

      {/* Search Users removed as requested */}

      {/* Notifications Panel */}
      {!isCollapsed && (
        <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold" style={{ color: 'var(--text)' }}>Notifications</h3>
            <button
              className="text-xs text-theme-secondary hover:text-heading"
              onClick={() => onNavigate?.('notifications')}
            >View all</button>
          </div>
          {notifications.length === 0 ? (
            <p className="text-xs text-theme-secondary">No upcoming events</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto pr-1 sm:max-h-none sm:overflow-visible">
              {notifications.slice(0, 1).map((n) => {
                let isUnseen = false;
                try {
                  const uidSeenKey = `auralink-notifs-seen:${String(user?._id || user?.id || 'anon')}`;
                  const rawSeen = localStorage.getItem(uidSeenKey) || '[]';
                  const arrSeen: string[] = Array.isArray(JSON.parse(rawSeen)) ? JSON.parse(rawSeen) : [];
                  const k = n.kind ? `${n.kind}:${n.id}` : `${n.id}`;
                  isUnseen = n.id ? !arrSeen.includes(k) : false;
                } catch {}
                const baseBtn = "w-full text-left px-2 sm:px-3 py-2 rounded-lg themed-card border hover:shadow-sm transition";
                const stateCls = isUnseen ? "ring-1 ring-cyan-500/30 bg-cyan-500/5" : "opacity-95 hover:opacity-100";
                return (
                <button
                  key={n.id}
                  className={`${baseBtn} ${stateCls}`}
                  style={{ borderColor: 'var(--border)' }}
                  onClick={() => {
                    // Mark this notification as seen and clear badge
                    try {
                      const uidSeenKey = `auralink-notifs-seen:${String(user?._id || user?.id || 'anon')}`;
                      const raw = localStorage.getItem(uidSeenKey) || '[]';
                      const arr: string[] = Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
                      const k = n.kind ? `${n.kind}:${n.id}` : `${n.id}`;
                      if (n.id && !arr.includes(k)) arr.push(k);
                      localStorage.setItem(uidSeenKey, JSON.stringify(arr));
                      localStorage.setItem('auralink-unread-notifs', '0');
                      window.dispatchEvent(new CustomEvent('auralink-unread-notifs.update', { detail: { count: 0 } }));
                      try { localStorage.removeItem('auralink-last-follower'); } catch {}
                      setUnreadNotifs(0);
                    } catch {}
                    try { localStorage.setItem('auralink-highlight-event', n.id); } catch {}
                    try { localStorage.setItem('auralink-discover-category', 'sports'); } catch {}
                    onNavigate?.('discover');
                  }}
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    {n.avatar ? (
                      <Avatar
                        src={makeAvatarUrl(n.avatar)}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-md object-cover"
                        alt={n.title || 'Notification'}
                      />
                    ) : (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md" style={{ background: 'var(--muted)' }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] sm:text-xs font-semibold line-clamp-2" style={{ color: 'var(--text)' }}>{n.title}</div>
                      <div className="text-[11px] sm:text-xs text-theme-secondary line-clamp-2">{n.message} • {n.time}</div>
                    </div>
                  </div>
                </button>
              );})}
            </div>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="flex-1 p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        {!isCollapsed && (
          <h3 className="text-sm font-bold mb-3" style={{ color: 'var(--text)' }}>Quick Stats</h3>
        )}
        
        <div className="space-y-2">
          {loading && !error ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-16 bg-slate-800 rounded-xl"></div>
                </div>
              ))}
            </>
          ) : (
            stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  onClick={stat.onClick}
                  className={`
                    relative rounded-xl p-3 cursor-pointer border bg-gradient-to-r ${stat.color}
                    transition-all duration-300 hover:scale-105 hover:shadow-xl hover:border-emerald-500/40
                    ${isCollapsed ? 'aspect-square' : ''}
                  `}
                  style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
                >
                  <div className="relative z-10">
                    <div className={`flex ${isCollapsed ? 'flex-col items-center justify-center h-full' : 'items-center justify-between'}`}>
                      <div className={isCollapsed ? 'mb-1' : ''}>
                        <Icon className="w-5 h-5 text-emerald-400" />
                      </div>
                      {!isCollapsed && (
                        <div className="text-right">
                          <p className="text-xl font-bold" style={{ color: 'var(--text)' }}>
                            {stat.value}
                          </p>
                          <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                            {stat.label}
                          </p>
                        </div>
                      )}
                    </div>
                    {isCollapsed && (
                      <div className="text-center">
                        <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>{stat.value}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {error && !loading && !isCollapsed && (
          <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-400 text-center">
              Unable to load stats
            </p>
          </div>
        )}
      </div>

      {/* Assistant Toggle */}
      <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
        <button
          onClick={() => {
            const next = !assistantHidden;
            setAssistantHidden(next);
            try { localStorage.setItem('teakonn.assistantHidden', next ? 'true' : 'false'); } catch {}
            window.dispatchEvent(new CustomEvent('teakonn.assistant.toggle', { detail: { hidden: next } }));
          }}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-center gap-2'} px-4 py-2 themed-card border transition-all`}
          title={assistantHidden ? 'Show Assistant' : 'Hide Assistant'}
          style={{ borderColor: 'var(--border)' }}
        >
          <Bot className="w-4 h-4 text-accent" />
          {!isCollapsed && <span>{assistantHidden ? 'Show Assistant' : 'Hide Assistant'}</span>}
        </button>
      </div>

      {/* Logout and Footer */}
      <div className="p-4 border-t space-y-3" style={{ borderColor: 'var(--border)' }}>
        {!isCollapsed && (
          <button
            onClick={loadUserStats}
            disabled={loading}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-all text-xs font-medium disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Stats'}
          </button>
        )}
        
        <button
          onClick={onLogout}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'justify-center gap-2'} px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all font-medium`}
        >
          <LogOut className="w-4 h-4" />
          {!isCollapsed && <span>Log Out</span>}
        </button>

        {!isCollapsed && (
          <div className="text-center pt-2">
            <p className="text-xs text-slate-500">© {new Date().getFullYear()}</p>
            <p className="text-xs font-semibold text-slate-400 mt-1">TeaKonn Devs</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Toggle Button */}
      <MobileToggle />

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Unified Sidebar */}
      <div
        className={`
          fixed lg:relative top-0 left-0 h-screen
          border-r shadow-2xl
          transition-all duration-300 z-40 flex-shrink-0
          ${isCollapsed ? 'lg:w-20' : 'lg:w-80'} w-full
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ 
          background: 'var(--sidebar)',
          borderColor: 'var(--border)'
        }}
      >
        <div className="relative h-full">
          <CollapseToggle />
          <SidebarContent />
        </div>
      </div>
    </>
  );
}
